import React, { useState, useEffect } from 'react';
import { fetchAllUsers, adminResetUserPassword } from '../api';
import axios from 'axios';
import { 
    FaUserShield, FaKey, FaCheckCircle, FaSearch, 
    FaExclamationTriangle, FaUserLock, FaLockOpen, FaLock
} from 'react-icons/fa';

const AdminResetPage = ({ currentUser }) => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [search, setSearch] = useState("");
    
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    
    const [message, setMessage] = useState(null);

    useEffect(() => { loadUsers(); }, []);

    useEffect(() => {
        setFilteredUsers(
            users.filter(u => 
                u.username.toLowerCase().includes(search.toLowerCase()) || 
                (u.fullName && u.fullName.toLowerCase().includes(search.toLowerCase()))
            )
        );
    }, [search, users]);

    const loadUsers = async () => {
        try {
            const res = await fetchAllUsers();
            setUsers(res.data);
            if (selectedUser) {
                const updatedSelectedUser = res.data.find(u => u._id === selectedUser._id);
                if (updatedSelectedUser) setSelectedUser(updatedSelectedUser);
            }
        } catch (err) { setMessage({ type: 'error', text: "Erreur lors du chargement des utilisateurs." }); }
    };

    const handleReset = async (e) => {
        e.preventDefault();
        if (!selectedUser || !newPassword) return;
        if (!window.confirm(`⚠️ ATTENTION : Forcer la réinitialisation du mot de passe de ${selectedUser.fullName} ?`)) return;

        try {
            await adminResetUserPassword({ targetUserId: selectedUser._id, newPassword: newPassword });
            setMessage({ type: 'success', text: `✅ Le mot de passe de ${selectedUser.fullName} a été mis à jour.` });
            setNewPassword('');
            setTimeout(() => setMessage(null), 5000);
        } catch (err) {
            setMessage({ type: 'error', text: "Erreur technique lors de la réinitialisation." });
        }
    };

    const handleTogglePause = async (userToToggle) => {
        const isCurrentlyActive = userToToggle.isActive !== false;
        const actionText = isCurrentlyActive ? "VERROUILLER" : "DÉVERROUILLER";
        
        if (!window.confirm(`Voulez-vous vraiment ${actionText} l'accès de ${userToToggle.fullName} ?`)) return;

        try {
            const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const storedData = JSON.parse(localStorage.getItem('facsmart_user'));
            const token = storedData?.token;

            const res = await axios.put(
                `${API_BASE}/auth/toggle-pause`, 
                { targetUserId: userToToggle._id }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            const newActiveStatus = !isCurrentlyActive;
            
            setUsers(prevUsers => prevUsers.map(u => 
                u._id === userToToggle._id ? { ...u, isActive: newActiveStatus } : u
            ));

            if (selectedUser && selectedUser._id === userToToggle._id) {
                setSelectedUser(prev => ({ ...prev, isActive: newActiveStatus }));
            }

            setMessage({ type: 'success', text: `✅ ${res.data.message}` });
            setTimeout(() => setMessage(null), 5000);
            
        } catch (err) {
            setMessage({ type: 'error', text: err.response?.data?.message || "Erreur lors du changement de statut." });
        }
    };

    return (
        <div className="container-fluid py-3 px-3 px-md-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', fontSize: '13px' }}>
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-2 text-center text-md-start">
                <div>
                    <h4 className="fw-bold mb-0" style={{ color: '#1A7A38' }}>
                        <FaUserShield className="me-2"/> ANNUAIRE & SÉCURITÉ
                    </h4>
                    <p className="text-muted small mb-0">
                        {currentUser?.role === 'superadmin' ? "Gestion de la sécurité et des accès" : "Consultation de l'annuaire du personnel"}
                    </p>
                </div>
            </div>

            {message && (
                <div className={`alert alert-sm py-2 rounded-0 shadow-sm alert-${message.type === 'error' ? 'danger' : 'success'}`}>
                    {message.text}
                </div>
            )}

            <div className="row g-3">
                <div className={`col-12 col-lg-${currentUser?.role === 'superadmin' ? '6' : '12'}`}>
                    <div className="card border-0 shadow-sm rounded-0 h-100">
                        <div className="card-header bg-dark text-white py-2 d-flex justify-content-between align-items-center">
                            <span className="fw-bold text-uppercase">Liste des Employés</span>
                            <span className="badge bg-light text-dark rounded-0">{filteredUsers.length}</span>
                        </div>
                        <div className="card-body p-0">
                            <div className="p-2 border-bottom bg-light">
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text bg-white rounded-0 border-end-0"><FaSearch className="text-muted"/></span>
                                    <input className="form-control rounded-0 border-start-0" placeholder="Rechercher..." value={search} onChange={e => setSearch(e.target.value)} />
                                </div>
                            </div>
                            
                            <div className="table-responsive" style={{ maxHeight: '500px' }}>
                                <table className="table table-sm table-hover align-middle mb-0 text-nowrap">
                                    <thead className="table-light sticky-top">
                                        <tr className="text-uppercase x-small fw-bold text-secondary">
                                            <th className="ps-3">Nom Complet</th>
                                            <th>Identifiant</th>
                                            <th>Rôle</th>
                                            <th className="text-center">Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredUsers.length === 0 ? (
                                            <tr><td colSpan="4" className="text-center text-muted py-4"><em>Aucun résultat.</em></td></tr>
                                        ) : (
                                            filteredUsers.map(u => (
                                                <tr key={u._id} 
                                                    className={`cursor-pointer ${selectedUser?._id === u._id ? 'table-active' : ''}`}
                                                    onClick={() => { setSelectedUser(u); setMessage(null); setNewPassword(''); }}
                                                    style={{ opacity: u.isActive === false ? 0.6 : 1 }}
                                                >
                                                    <td className="ps-3 fw-bold">{u.fullName}</td>
                                                    <td className="text-muted">{u.username}</td>
                                                    <td>
                                                        <span className="badge bg-light text-dark border rounded-0" style={{fontSize: '10px'}}>{u.role}</span>
                                                    </td>
                                                    <td className="text-center">
                                                        <button 
                                                            type="button"
                                                            className="btn btn-link p-0 m-0 border-0" 
                                                            onClick={(e) => { 
                                                                e.stopPropagation(); 
                                                                handleTogglePause(u); 
                                                            }}
                                                            title={u.isActive !== false ? "Cliquez pour verrouiller l'accès" : "Cliquez pour déverrouiller l'accès"}
                                                        >
                                                            {u.isActive !== false ? (
                                                                <FaLockOpen className="text-success fs-5" /> 
                                                            ) : (
                                                                <FaLock className="text-danger fs-5" />
                                                            )}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {currentUser?.role === 'superadmin' && (
                    <div className="col-12 col-lg-6">
                        <div className="card border-0 shadow-sm rounded-0 h-100">
                            <div className="card-header bg-white py-2 border-bottom fw-bold text-secondary text-uppercase">
                                <FaUserLock className="me-2"/> Options de Sécurité
                            </div>
                            <div className="card-body p-3 p-md-4 bg-light">
                                {selectedUser ? (
                                    <div className="card border-0 shadow-sm rounded-0 p-3">
                                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center border-bottom pb-3 mb-3 gap-2">
                                            <div>
                                                <h5 className="fw-bold mb-0 text-dark">
                                                    {selectedUser.fullName}
                                                    {selectedUser.isActive === false && <span className="badge bg-danger ms-2" style={{fontSize: '10px'}}>VERROUILLÉ</span>}
                                                </h5>
                                                <div className="text-muted small">{selectedUser.username}</div>
                                            </div>
                                            <button 
                                                type="button"
                                                className={`btn btn-sm rounded-0 fw-bold w-100 w-sm-auto ${selectedUser.isActive !== false ? 'btn-outline-danger' : 'btn-success'}`}
                                                onClick={() => handleTogglePause(selectedUser)}
                                            >
                                                {selectedUser.isActive !== false ? <><FaLock className="me-1"/> VERROUILLER L'ACCÈS</> : <><FaLockOpen className="me-1"/> DÉVERROUILLER</>}
                                            </button>
                                        </div>

                                        <form onSubmit={handleReset}>
                                            <label className="fw-bold text-muted small mb-1">FORCER LE MOT DE PASSE *</label>
                                            <div className="input-group input-group-sm mb-2">
                                                <span className="input-group-text bg-white rounded-0"><FaKey className="text-muted"/></span>
                                                <input 
                                                    type="text" className="form-control rounded-0" 
                                                    placeholder="Saisir le nouveau mot de passe"
                                                    value={newPassword} onChange={e => setNewPassword(e.target.value)}
                                                    required autoComplete="off"
                                                />
                                            </div>
                                            <div className="text-danger x-small mb-3"><FaExclamationTriangle className="me-1"/>Écrase immédiatement l'ancien mot de passe.</div>
                                            <button type="submit" className="btn btn-sm btn-primary rounded-0 fw-bold w-100" disabled={!newPassword}>
                                                VALIDER LA RÉINITIALISATION
                                            </button>
                                        </form>
                                    </div>
                                ) : (
                                    <div className="text-center text-muted opacity-50 py-5">
                                        <FaUserShield size={40} className="mb-2"/>
                                        <div className="fw-bold">Aucun employé sélectionné</div>
                                        <div className="small">Sélectionnez une ligne dans le tableau pour agir.</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminResetPage;